import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAttributeFormatter } from 'utils/attributeFormatter';
import { MeasureUnitInfo } from 'components/measure-unit/MeasureUnitInfo';
import { Interpretation, MeasureComparison } from 'components/MeasureComparison';
import { useAppStore } from 'hooks/state/useAppStore';
import { ChartTooltipPopperFrame } from './ChartTooltipPopperFrame';
import type { ChartTooltipPopperFrameHandle } from './ChartTooltipPopperFrame';
import { ChartRenderer } from './renderer/ChartRenderer';
import { ChartTooltipContent } from './renderer/ChartTooltipContent';
import { ChartTooltipData } from './renderer/ChartTooltipData';
import { MeasureFormat } from '../measure-unit/MeasureFormat';
import { MeasureComparisonUtil } from '../measure-comparison/MeasureComparisonUtil';
import { MeasureUnitCode } from '../measure-unit/MeasureUnitCode';
import { ChartTooltipCard } from './ChartTooltipCard';
import type { ChartTooltipCardRow } from './ChartTooltipCard';

export function ChartTooltip({ renderer }: { renderer: ChartRenderer }) {
  const currentRenderer = renderer;
  const customTooltip = currentRenderer.customTooltip;

  const { attributeFormatter } = useAttributeFormatter();
  const { dateRange } = useAppStore();
  const [data, setData] = useState(undefined as ChartTooltipData | undefined);
  const popperFrameRef = useRef(null as ChartTooltipPopperFrameHandle | null);

  currentRenderer.tooltipContent = new ChartTooltipContent(
    (x: number, y: number) => popperFrameRef.current?.openAt(x, y),
    () => popperFrameRef.current?.close(),
    setData
  );

  const isSecondaryAsLine: boolean = currentRenderer.secondaryAsLine;

  function renderHeaderValue(tooltipData: ChartTooltipData) {
    const unit = tooltipData.values?.unit;
    if (!unit) return null;

    if (tooltipData.breakdownData) {
      // Render the trend value in the header as stylized text grouping data...
      return <MeasureFormat value={tooltipData.headerData?.sum ?? 0} unit={unit} />;
    }

    /* ...otherwise, render the trend value in the header as a chip component
     * when we are not grouping data.
     */
    return (
      <MeasureComparison
        value={tooltipData.data?.value ?? 0}
        comparisonValue={tooltipData.data?.comparisonValue ?? 0}
        unit={unit}
        interpretation={tooltipData.values?.interpretation ?? Interpretation.POSITIVE}
      />
    );
  }

  const renderHeaderAccessory = (tooltipData: ChartTooltipData, unit: MeasureUnitInfo) => {
    if (tooltipData.breakdownData) {
      return (
        <div style={{ paddingLeft: 8, paddingRight: 8, textAlign: 'right' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 300,
              lineHeight: '15px',
              whiteSpace: 'nowrap',
            }}
          >
            {tooltipData.headerData?.caption}
          </div>
          <strong
            style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 600,
              lineHeight: '24px',
              whiteSpace: 'nowrap',
            }}
          >
            <MeasureFormat value={tooltipData.headerData?.sum} unit={unit} />
          </strong>
        </div>
      );
    }

    if (tooltipData.data && tooltipData.withComparison) {
      return renderHeaderValue(tooltipData);
    }

    return null;
  };

  const getComparisonLabel = (tooltipData: ChartTooltipData) =>
    tooltipData.comparisonLabel ?? (
      <FormattedMessage
        id={dateRange?.comparison?.presetLabel ? dateRange?.comparison?.presetLabel : 'datepicker.options.main.custom'}
        defaultMessage="Compared to"
      />
    );

  const buildDefaultRows = (tooltipData: ChartTooltipData, unit: MeasureUnitInfo): ChartTooltipCardRow[] => {
    const rows: ChartTooltipCardRow[] = [];
    const formatValue = (value: number | undefined) => (
      <MeasureFormat value={value} unit={unit} config={tooltipData.values?.formatConfig} />
    );

    if (tooltipData.data) {
      rows.push({
        key: 'current',
        label: attributeFormatter(tooltipData.data.label),
        values: tooltipData.withShares
          ? [
              {
                key: 'share',
                content: (
                  <MeasureFormat
                    value={tooltipData.data.value / (tooltipData.headerData?.sum ?? 1)}
                    unit={MeasureUnitCode.PERCENT}
                    config={{ decimalPlaces: 2 }}
                  />
                ),
                minWidth: 98,
              },
              {
                key: 'value',
                content: formatValue(tooltipData.data.value),
              },
            ]
          : [
              {
                key: 'value',
                content: formatValue(tooltipData.data.value),
              },
            ],
      });
    }

    if (!isSecondaryAsLine && tooltipData.secondaryData) {
      rows.push({
        key: 'secondary',
        label: attributeFormatter(tooltipData.secondaryData.label),
        values: [
          {
            key: 'value',
            content: (
              <MeasureFormat
                value={tooltipData.secondaryData.value}
                unit={
                  (tooltipData.secondaryValues?.unit?.code ? tooltipData.secondaryValues : tooltipData.values)?.unit ??
                  unit
                }
              />
            ),
          },
        ],
      });
    }

    if (tooltipData.data && tooltipData.withComparison) {
      rows.push({
        key: 'comparison',
        label: getComparisonLabel(tooltipData),
        values: tooltipData.withShares
          ? [
              {
                key: 'share',
                content: (
                  <MeasureFormat
                    value={MeasureComparisonUtil.calcValue(
                      tooltipData.data.value / (tooltipData.headerData?.sum ?? 1),
                      (tooltipData.data.comparisonValue ?? 0) / (tooltipData.headerData?.comparisonSum ?? 1)
                    )}
                    unit={MeasureUnitCode.PERCENT}
                    config={{ isDiff: true, hasSign: true }}
                  />
                ),
                minWidth: 98,
              },
              {
                key: 'value',
                content: formatValue(tooltipData.data.comparisonValue),
              },
            ]
          : [
              {
                key: 'value',
                content: formatValue(tooltipData.data.comparisonValue),
              },
            ],
      });
    }

    tooltipData.breakdownData
      ?.filter(bd => !bd.isOther)
      .forEach(breakdownData =>
        rows.push({
          key: breakdownData.label,
          leadingValue: !isSecondaryAsLine ? (
            <MeasureFormat
              value={breakdownData.percentage}
              unit={MeasureUnitCode.PERCENT}
              config={{ hasSign: false }}
            />
          ) : undefined,
          leadingValueColor: breakdownData.chartColor,
          label: attributeFormatter(breakdownData.label),
          title: attributeFormatter(breakdownData.label),
          values: [
            {
              key: 'value',
              content: <MeasureFormat value={breakdownData.value} unit={unit} />,
            },
          ],
        })
      );

    tooltipData.breakdownData
      ?.filter(bd => bd.isOther)
      .forEach(breakdownData =>
        rows.push({
          key: breakdownData.label,
          leadingValue: !isSecondaryAsLine ? (
            <MeasureFormat
              value={breakdownData.percentage}
              unit={MeasureUnitCode.PERCENT}
              config={{ hasSign: false }}
            />
          ) : undefined,
          leadingValueColor: breakdownData.chartColor,
          label: attributeFormatter(breakdownData.label),
          title: attributeFormatter(breakdownData.label),
          values: [
            {
              key: 'value',
              content: <MeasureFormat value={breakdownData.value} unit={unit} />,
            },
          ],
        })
      );

    if (isSecondaryAsLine && tooltipData.secondaryData) {
      rows.push({
        key: 'secondary-line',
        label: attributeFormatter(tooltipData.secondaryData.label),
        withTopDivider: rows.length > 0,
        values: [
          {
            key: 'value',
            content: (
              <MeasureFormat
                value={tooltipData.secondaryData.value}
                unit={
                  (tooltipData.secondaryValues?.unit?.code ? tooltipData.secondaryValues : tooltipData.values)?.unit ??
                  unit
                }
              />
            ),
          },
        ],
      });
    }

    return rows;
  };

  const renderDefaultTooltip = () => {
    if (!data || data.rows) return <span>-</span>;

    const unit = data.values?.unit;
    if (!unit) return <span>-</span>;

    return (
      <ChartTooltipCard
        title={attributeFormatter(data.headerData?.label)}
        rows={buildDefaultRows(data, unit)}
        headerAccessory={renderHeaderAccessory(data, unit)}
      />
    );
  };

  return (
    <ChartTooltipPopperFrame ref={popperFrameRef} placement={customTooltip?.placement ?? 'right'} withDropShadow>
      {data && customTooltip?.render ? customTooltip.render(data) : renderDefaultTooltip()}
    </ChartTooltipPopperFrame>
  );
}
